import { FC, ReactNode, useCallback, useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { getRouteApi, useLocation } from '@tanstack/react-router'
import { useDebounce } from 'commons/hooks/common'
import { alertsQueryOptions } from '~bank-alerts/query'
import { AlertsPayload, AlertsResponse } from '~bank-alerts/types'

import { AlertsContext, TablePrefix } from './AlertsContext'

type AlertsContextProviderProps = {
  children: ReactNode
}

export const AlertsContextProvider: FC<AlertsContextProviderProps> = ({ children }) => {
  const routeApi = getRouteApi('/_app/alerts')

  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()
  const hash = useLocation({ select: (s) => s.hash })

  const [loadingTables, setLoadingTables] = useState<Set<TablePrefix>>(new Set())

  const alertsQuery = useQuery<AlertsResponse>(alertsQueryOptions(search))

  const normalizeSearch = (params: Partial<AlertsPayload>) =>
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) as Partial<AlertsPayload>

  const debouncedSearch = useDebounce((searchParams: Partial<AlertsPayload>) =>
    navigate({
      to: '.',
      search: (prev) => normalizeSearch({ ...prev, ...searchParams }),
      hash,
      hashScrollIntoView: false,
      resetScroll: false,
      replace: true
    })
  )

  useEffect(() => {
    if (alertsQuery.isFetched && !alertsQuery.isRefetching && !debouncedSearch.pending()) {
      setLoadingTables(new Set())
    }
    return () => {
      debouncedSearch.cancel()
    }
  }, [alertsQuery.isFetched, alertsQuery.isFetching, alertsQuery.isRefetching, debouncedSearch])

  const isTableLoading = useCallback(
    (tablePrefix: TablePrefix) => {
      return loadingTables.has(tablePrefix)
    },
    [loadingTables]
  )

  const addLoadingTable = useCallback((tablePrefix: TablePrefix) => {
    setLoadingTables((prev) => new Set(prev).add(tablePrefix))
  }, [])

  if (!alertsQuery.data) return null

  const value = {
    data: alertsQuery.data,
    isTableLoading,
    successfullyLoaded: !!alertsQuery.data,
    search,
    invalidate: alertsQuery.refetch,
    onFilterChange: async (searchParams: Partial<AlertsPayload>, tablePrefix: TablePrefix) => {
      addLoadingTable(tablePrefix)
      return debouncedSearch(searchParams)
    },
    onPageChange: (page: number, size: number, prefix: TablePrefix) => {
      addLoadingTable(prefix)
      navigate({
        to: '.',
        search: (prev: Partial<AlertsPayload>) => ({
          ...prev,
          [`${prefix}Page`]: page,
          [`${prefix}Size`]: size
        }),
        hash,
        hashScrollIntoView: false,
        resetScroll: false,
        replace: true
      })
    },
    clear: () => {
      setLoadingTables(new Set())
      navigate({ to: '.', search: undefined, hash, hashScrollIntoView: false, resetScroll: false, replace: true })
    }
  }

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>
}
